import AirBridgeMark from './AirBridgeMark';
import { ACCENT, SURFACE } from '../theme/tokens';

// The between-pages and boot loader (FD-466).
//
// Replaces `FlightDeckLoader`, whose artwork was the orange FlightDeck chevron
// and a navy aircraft on the brand kit's warm `#F4F2EE`. None of that survives
// the rebrand, and leaving it would have been the FD-270 defect — a rename that
// half-applies, with the old identity still spinning on every navigation.
//
// The MOTION CHARACTER is kept deliberately: an arc that draws, then the mark
// resolving into it, on a ~2.4 s loop. It is the one part of the old loader that
// was about pace rather than identity, and an operator who waits on this a
// hundred times a day has already learned its rhythm.
//
// `prefers-reduced-motion` collapses it to the resolved static mark — the
// contract's rule that motion is never the sole carrier of state.

/**
 * The ground this loader is drawn on.
 *
 * Exported because `BootSplash` paints a full-screen field behind it and the two
 * must not show a seam. It is `SURFACE.canvas` rather than a hardcoded value:
 * unlike the retired mark, this artwork has no canvas of its own to be
 * authoritative about, so it belongs to the theme.
 */
export const LOADER_CANVAS = SURFACE.canvas;

type AirBridgeLoaderProps = {
  size?: number;
  className?: string;
  label?: string;
};

export function AirBridgeLoader({
  size = 96,
  className,
  label = 'Loading AirBridge',
}: AirBridgeLoaderProps) {
  return (
    <div
      className={className}
      role="status"
      aria-label={label}
      style={{ width: size, height: size, position: 'relative' }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 120 120"
        aria-hidden="true"
        focusable="false"
        style={{ position: 'absolute', inset: 0 }}
      >
        <style>{`
          .ab-arc { fill:none; stroke:${ACCENT.base}; stroke-width:3; stroke-linecap:round;
            stroke-dasharray:190; animation:abDraw 2.4s ease-in-out infinite; }
          .ab-trail { fill:none; stroke:${ACCENT.highlight}; stroke-width:2; stroke-linecap:round;
            stroke-dasharray:6 9; animation:abTrail 2.4s ease infinite; }
          @keyframes abDraw {
            0%   { stroke-dashoffset:190; opacity:.15 }
            22%,74% { stroke-dashoffset:0; opacity:1 }
            100% { stroke-dashoffset:0; opacity:0 }
          }
          @keyframes abTrail { 0%,14%{opacity:0} 30%,74%{opacity:.7} 100%{opacity:0} }
          @media (prefers-reduced-motion: reduce) {
            .ab-arc,.ab-trail { animation:none; opacity:0; }
          }
        `}</style>
        {/* The bridge: a span rising from one shore to the other. */}
        <path className="ab-arc" d="M12 92C34 34 86 34 108 92" />
        <path className="ab-trail" d="M12 92C34 34 86 34 108 92" />
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <AirBridgeMark size={size * 0.34} />
      </div>
    </div>
  );
}
